import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { CancelButton, FormButton } from "../../../../components/Botton";
import Loader from "../../../../components/Loader";
import useAddCoupon, { IAddCoupon, IStudent } from "./useAddCoupon";

const AddCoupon: React.FC = () => {
  const {
    formData,
    loading,
    couponCategories,
    handleChange,
    handleSubmit,
    handleCancel,
    selectedStudents,
    searchStudents,
    handleStudentSelect,
    handleRemoveStudent,
    handleAssignmentTypeChange ,
    assignmentType ,
    
  } = useAddCoupon();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<IStudent[]>([]);

  const {
    register,
    handleSubmit: useFormSubmit,
    formState: { errors },
  } = useForm<IAddCoupon>();

  const discountTypes = [
    { id: "PERCENTAGE", name: "Percentage" },
    { id: "FIXED", name: "Fixed Amount" },
  ];

  const handleSearchChange = (e:any) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = async () => {
    if (searchTerm.trim().length > 2) {
      const results = await searchStudents(searchTerm);
      setSearchResults(results);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchResults([]);
  };

   
  

  return (
    <div className="flex flex-col items-center w-full h-screen p-8">
      <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full">
        <div className="border-b-2 w-[100%]">
          <h1 className="text-3xl mb-2 text-gray-700 ml-5">Add Coupon</h1>
        </div>
        {loading && <Loader />}
        <form onSubmit={useFormSubmit(handleSubmit)} className="space-y-6">
          <fieldset className="m-2 rounded border-opacity-60 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-4">
              {/* Coupon Type/Category */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2" htmlFor="type">
                  Coupon Category:
                </label>
                <select
                  id="type"
                  {...register("type", { required: true })}
                  onChange={handleChange}
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value="">Select Category</option>
                  {couponCategories.map((category) => (
                    <option key={category._id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.type && (
                  <span className="text-red-500 text-xs italic">
                    Coupon category is required
                  </span>
                )}
              </div>

              {/* Discount Type */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2" htmlFor="discountType">
                  Discount Type:
                </label>
                <select
                  id="discountType"
                  {...register("discountType", { required: true })}
                  onChange={handleChange}
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                >
                  {discountTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {errors.discountType && (
                  <span className="text-red-500 text-xs italic">
                    Discount type is required
                  </span>
                )}
              </div>

              {/* Discount Value */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2" htmlFor="discountValue">
                  Discount Value:
                </label>
                <input
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                  type="number"
                  id="discountValue"
                  min={0}
                  step={formData.discountType === "PERCENTAGE" ? 1 : 0.01}
                  max={formData.discountType === "PERCENTAGE" ? 100 : undefined}
                  {...register("discountValue", { required: true, min: 0, max: formData.discountType === "PERCENTAGE" ? 100 : undefined })}
                  value={formData.discountValue}
                  onChange={handleChange}
                />
                {errors.discountValue && (
                  <span className="text-red-500 text-xs italic">
                    Valid discount value is required
                  </span>
                )}
              </div>

              {/* Max Discount Amount */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2" htmlFor="maxDiscountAmount">
                  Max Discount Amount:
                </label>
                <input
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                  type="number"
                  id="maxDiscountAmount"
                  min={0}
                  step={0.01}
                  {...register("maxDiscountAmount")}
                  value={formData.maxDiscountAmount === null ? "" : formData.maxDiscountAmount}
                  onChange={handleChange}
                  placeholder="Leave empty for no limit"
                />
              </div>

              {/* Valid From */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2" htmlFor="validFrom">
                  Valid From:
                </label>
                <input
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                  type="date"
                  id="validFrom"
                  {...register("validFrom", { required: true })}
                  value={formData.validFrom.toString()}
                  onChange={handleChange}
                />
                {errors.validFrom && (
                  <span className="text-red-500 text-xs italic">
                    Start date is required
                  </span>
                )}
              </div>

              {/* Valid Until */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2" htmlFor="validUntil">
                  Valid Until:
                </label>
                <input
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                  type="date"
                  id="validUntil"
                  {...register("validUntil", { required: true })}
                  value={formData.validUntil.toString()}
                  onChange={handleChange}
                />
                {errors.validUntil && (
                  <span className="text-red-500 text-xs italic">
                    End date is required
                  </span>
                )}
              </div>

              {/* Usage Limit */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2" htmlFor="usageLimit">
                  Usage Limit:
                </label>
                <input
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                  type="number"
                  id="usageLimit"
                  min={-1}
                  {...register("usageLimit")}
                  value={formData.usageLimit}
                  onChange={handleChange}
                />
                <span className="text-gray-500 text-xs italic">
                  Use -1 for unlimited, 1 for one-time use
                </span>
              </div>

              {/* Is Active */}
              <div className="flex items-center">
                <input
                  className="mr-2 leading-tight"
                  type="checkbox"
                  id="isForNewUsersOnly"
                  {...register("isForNewUsersOnly")}
                  checked={formData.isForNewUsersOnly}
                  onChange={handleChange}
                />
                <label className="text-gray-700 text-base font-semibold" htmlFor="isForNewUsersOnly">
                  Is for Only New Students?
                </label>
              </div>
            </div>
          </fieldset>

          {/* Coupon Assignment Section */}
          <fieldset className="m-2 rounded border border-gray-300 p-4">
            <legend className="text-gray-700 text-lg font-semibold px-2">Coupon Assignment</legend>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="assignEveryone"
                    name="assignmentType"
                    value="everyone"
                    checked={assignmentType === "everyone"}
                    onChange={(e) => handleAssignmentTypeChange(e.target.value as "everyone" | "specific")}
                    className="mr-2"
                  />
                  <label htmlFor="assignEveryone" className="text-gray-700">Assign to everyone</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="assignSpecific"
                    name="assignmentType"
                    value="specific"
                    checked={assignmentType === "specific"}
                    onChange={(e) => handleAssignmentTypeChange(e.target.value as "everyone" | "specific")}
                    className="mr-2"
                  />
                  <label htmlFor="assignSpecific" className="text-gray-700">Assign to specific students</label>
                </div>
              </div>

              {assignmentType === "specific" && (
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="text"
                      placeholder="Search students by email"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="appearance-none border rounded py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline flex-grow mr-2"
                    />
                    <button
                      type="button"
                      onClick={handleSearch}
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mr-2"
                    >
                      Search
                    </button>
                    {searchResults.length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="mt-2 border rounded max-h-40 overflow-y-auto p-2">
                      <p className="font-semibold mb-2">Search Results:</p>
                      <ul className="divide-y divide-gray-200">
                        {searchResults.map((student) => (
                          <li key={student?._id} className="py-2 flex justify-between items-center">
                            <span>{student.email}</span>
                            <button
                              type="button"
                              onClick={() => handleStudentSelect(student)}
                              className="bg-green-500 hover:bg-green-700 text-white text-sm py-1 px-2 rounded"
                            >
                              Add
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Selected Students */}
                  {selectedStudents.length > 0 && (
                    <div className="mt-4">
                      <p className="font-semibold mb-2">Selected Students:</p>
                      <div className="border rounded p-2 max-h-40 overflow-y-auto">
                        <ul className="divide-y divide-gray-200">
                          {selectedStudents.map((student) => (
                            <li key={student._id} className="py-2 flex justify-between items-center">
                              <span>{student.email}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveStudent(student._id)}
                                className="bg-red-500 hover:bg-red-700 text-white text-sm py-1 px-2 rounded"
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </fieldset>

          <div className="flex justify-start mt-6 ml-10">
            <FormButton className="py-2 px-4 rounded mr-4" type="submit">
              ADD
            </FormButton>
            <CancelButton className="py-2 px-4 rounded" onClick={handleCancel} type="button">
              Cancel
            </CancelButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCoupon;